import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { PrincipalInterestChart } from '../../../components/charts/PrincipalInterestChart';
import { WealthMilestoneChart } from '../../../components/charts/WealthMilestoneChart';
import { formatCurrency } from '../../../components/charts/d3/format';
import { PageHeader } from '../../../components/layout/PageHeader';
import { WebScreenBody } from '../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../components/layout/WebWidgetGrid';
import { WidgetRefreshScope } from '../../../contexts/WidgetRefreshContext';
import { SampleDataBanner } from '../../../components/ui/SampleDataBanner';
import { GlassButton } from '../../../components/ui/GlassButton';
import { GlassCard } from '../../../components/ui/GlassCard';
import { CompoundYearTable } from '../../../components/wealth';
import { useSampleData } from '../../../contexts/SampleDataContext';
import { useHousehold } from '../../../contexts/HouseholdContext';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { useAppRefresh } from '../../../hooks/useAppRefresh';
import { useHomeDashboard } from '../../../hooks/useHomeDashboard';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  MAX_ANNUAL_RATE_PCT,
  MAX_WEALTH_TARGET,
  MIN_ANNUAL_RATE_PCT,
  MIN_WEALTH_TARGET,
  useInvestmentCompounding,
} from '../../../hooks/useInvestmentCompounding';
import {
  formatMilestoneYears,
  formatWealthAmount,
} from '../../../utils/investmentCompounding';
import { ValueStepper } from '../../Onboarding/components/ValueStepper';

const TARGET_OPTIONS = [500_000, 1_000_000, 2_000_000];

export function GrowTabScreen() {
  const { isSampleMode } = useSampleData();
  const { core } = useHousehold();
  const { netWorth } = useHomeDashboard();
  const { refreshAll, refreshing } = useAppRefresh();
  const pageRefresh = () => {
    void refreshAll('grow_page');
  };
  const startYear = Number(core.profile.asOfMonth.slice(0, 4)) || new Date().getFullYear();
  const model = useInvestmentCompounding(netWorth.assets, { startYear });

  const {
    principal,
    annualContribution,
    annualRatePct,
    years,
    tableRows,
    compoundingKickInYear,
    milestonePlan,
    targetAmount,
    setPrincipal,
    nudgePrincipal,
    setAnnualContribution,
    nudgeAnnualContribution,
    setAnnualRatePct,
    nudgeAnnualRatePct,
    setYears,
    setTargetAmount,
    nudgeTargetAmount,
    resetToPortfolio,
    resetAssumptions,
    seedPrincipal,
    seedAnnualContribution,
    seedExpectedRatePct,
  } = model;

  const endBalance = tableRows[tableRows.length - 1]?.endBalance ?? principal;
  const totalGrowth = tableRows[tableRows.length - 1]?.cumulativeGrowth ?? 0;

  return (
    <WebScreenBody>
      {isSampleMode && <SampleDataBanner />}

      <PageHeader
        title="Grow"
        subtitle={`See how long each ${formatWealthAmount(milestonePlan.step)} takes — early years feel slow; later milestones arrive faster as compounding accelerates.`}
        onRefresh={pageRefresh}
        refreshing={refreshing}
      />

      <WidgetRefreshScope refresh={pageRefresh} refreshing={refreshing}>
      <WebWidgetGrid>
        <WebWidgetCell>
          <GlassCard style={styles.card}>
            <View style={styles.headerRow}>
              <Text style={styles.sectionTitle}>Your plan</Text>
              <GlassButton variant="ghost" size="sm" onPress={resetAssumptions}>
                Reset
              </GlassButton>
            </View>
            <Text style={styles.sectionHint}>
              Seeded from holdings
              {seedPrincipal > 0 ? ` (${formatCurrency(seedPrincipal)}` : ''}
              {seedAnnualContribution > 0
                ? ` · ${formatCurrency(seedAnnualContribution)}/yr`
                : ''}
              {seedPrincipal > 0 || seedAnnualContribution > 0 ? ')' : ''}
              — expected return defaults to {DEFAULT_ANNUAL_RATE_PCT}%.
            </Text>
            <ValueStepper
              label="Starting investment"
              value={principal}
              prefix="$"
              min={0}
              onChange={setPrincipal}
              onDecrease={() => nudgePrincipal(-5000)}
              onIncrease={() => nudgePrincipal(5000)}
            />
            <ValueStepper
              label="Yearly contribution"
              value={annualContribution}
              prefix="$"
              min={0}
              onChange={setAnnualContribution}
              onDecrease={() => nudgeAnnualContribution(-1000)}
              onIncrease={() => nudgeAnnualContribution(1000)}
            />
            <ValueStepper
              label="Expected return"
              value={annualRatePct}
              suffix="%"
              decimals
              min={MIN_ANNUAL_RATE_PCT}
              max={MAX_ANNUAL_RATE_PCT}
              onChange={setAnnualRatePct}
              onDecrease={() => nudgeAnnualRatePct(-0.5)}
              onIncrease={() => nudgeAnnualRatePct(0.5)}
              hint={`Default ${DEFAULT_ANNUAL_RATE_PCT}% — portfolio suggests ${seedExpectedRatePct.toFixed(1)}%`}
            />
            <ValueStepper
              label="Target"
              value={targetAmount}
              prefix="$"
              min={MIN_WEALTH_TARGET}
              max={MAX_WEALTH_TARGET}
              onChange={setTargetAmount}
              onDecrease={() => nudgeTargetAmount(-50_000)}
              onIncrease={() => nudgeTargetAmount(50_000)}
              hint="Quick picks below, or type any amount"
            />
            <View style={styles.chipRow}>
              {TARGET_OPTIONS.map((amount) => (
                <GlassButton
                  key={amount}
                  variant="secondary"
                  size="sm"
                  selected={targetAmount === amount}
                  onPress={() => setTargetAmount(amount)}
                >
                  {formatWealthAmount(amount)}
                </GlassButton>
              ))}
            </View>
            <GlassButton variant="ghost" size="sm" onPress={resetToPortfolio}>
              Use portfolio amounts & return
            </GlassButton>
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell style={styles.glanceCell}>
          <GlassCard style={styles.glanceCard}>
            <View style={styles.glanceHeader}>
              <Text style={styles.sectionTitle}>At a glance</Text>
              <Text style={styles.sectionHint}>
                Snapshot for your selected horizon and target
              </Text>
            </View>
            <View style={styles.kpiRow}>
              <View style={styles.kpi}>
                <Text style={styles.kpiLabel}>Return</Text>
                <Text style={styles.kpiValue}>{annualRatePct.toFixed(1)}%</Text>
              </View>
              <View style={styles.kpi}>
                <Text style={styles.kpiLabel}>In {years} years</Text>
                <Text style={[styles.kpiValue, styles.accent]} numberOfLines={1}>
                  {formatCurrency(endBalance)}
                </Text>
              </View>
              <View style={styles.kpi}>
                <Text style={styles.kpiLabel}>Growth earned</Text>
                <Text style={styles.kpiValue} numberOfLines={1}>
                  {formatCurrency(totalGrowth)}
                </Text>
              </View>
            </View>
            {milestonePlan.reached && milestonePlan.totalYears != null && (
              <View style={styles.kickInBanner}>
                <Text style={styles.kickInTitle}>
                  {formatWealthAmount(targetAmount)} in{' '}
                  {formatMilestoneYears(milestonePlan.totalYears)} years
                </Text>
                <Text style={styles.kickInBody}>
                  At {annualRatePct.toFixed(1)}% with {formatCurrency(annualContribution)}/yr.
                </Text>
              </View>
            )}
            <View style={styles.horizonBlock}>
              <Text style={styles.sectionLabel}>Horizon</Text>
              <View style={styles.chipRow}>
                {[10, 15, 20, 30].map((y) => (
                  <GlassButton
                    key={y}
                    variant="secondary"
                    size="sm"
                    selected={years === y}
                    onPress={() => setYears(y)}
                  >
                    {`${y}y`}
                  </GlassButton>
                ))}
              </View>
            </View>
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <GlassCard style={styles.card}>
            <Text style={styles.sectionTitle}>
              Accumulating {formatWealthAmount(targetAmount)}
            </Text>
            <Text style={styles.sectionHint}>
              Labels show years between each {formatWealthAmount(milestonePlan.step)} — most of the
              calendar time is spent on the first stretch.
            </Text>
            <WealthMilestoneChart
              plan={milestonePlan}
              annualRatePct={annualRatePct}
              annualContribution={annualContribution}
              height={300}
            />
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <GlassCard style={styles.card}>
            <Text style={styles.sectionTitle}>Principal vs interest</Text>
            {compoundingKickInYear != null && (
              <View style={styles.kickInBanner}>
                <Text style={styles.kickInTitle}>
                  Compounding kick-in · {compoundingKickInYear}
                </Text>
                <Text style={styles.kickInBody}>
                  Growth first exceeds your {formatCurrency(annualContribution)} yearly contribution.
                </Text>
              </View>
            )}
            <PrincipalInterestChart rows={tableRows} height={220} />
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <GlassCard style={styles.card}>
            <Text style={styles.sectionTitle}>Year-by-year</Text>
            <Text style={styles.sectionHint}>
              Principal = your money. Interest = compounded earnings. ★ = kick-in year.
            </Text>
            <CompoundYearTable
              rows={tableRows}
              kickInYear={compoundingKickInYear}
              dense
            />
          </GlassCard>
        </WebWidgetCell>
      </WebWidgetGrid>
      </WidgetRefreshScope>
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  pageHeader: {
    marginBottom: spacing.sm,
  },
  pageTitle: {
    ...typography.title,
    color: colors.text,
    fontSize: 26,
  },
  pageSub: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: 4,
  },
  card: {
    gap: spacing.sm,
  },
  glanceCell: {
    alignSelf: 'flex-start',
  },
  glanceCard: {
    gap: spacing.md,
    padding: spacing.lg,
  },
  glanceHeader: {
    gap: 4,
  },
  horizonBlock: {
    gap: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
  },
  sectionHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  sectionLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  kpiRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.sm,
    width: '100%',
  },
  kpi: {
    flex: 1,
    minWidth: 0,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    paddingVertical: 12,
    paddingHorizontal: 12,
    justifyContent: 'center',
    gap: 6,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
    lineHeight: 12,
  },
  kpiValue: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 20,
    fontVariant: ['tabular-nums'],
  },
  accent: {
    color: colors.accent,
  },
  kickInBanner: {
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 4,
    alignSelf: 'stretch',
    flexGrow: 0,
  },
  kickInTitle: {
    color: colors.accent,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 18,
  },
  kickInBody: {
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
  },
});
